import React, { useEffect, useState } from 'react';
import accountStore from './AccountStore';
import DiscordAuthService from '../../services/DiscordAuthService';
import audioManager from '../../utils/filemanager/AudioManager';
import PlayerService from '../../services/PlayerService';
import FeatureService from '../../services/FeatureService';
import ReferralDashboard from './ReferralDashboard';
import AdminDashboard from './AdminDashboard';

const ProfileSidebar = ({ onClose, onShowLeaderboard, onShowStatistics, onLogout, profile }) => {
  const [showReferralDashboard, setShowReferralDashboard] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [volume, setVolume] = useState(audioManager.getVolume());
  const [prevVolume, setPrevVolume] = useState(() => {
    try {
      const saved = localStorage.getItem('neuranet-prev-volume');
      return saved !== null ? parseFloat(saved) : 1;
    } catch {
      return 1;
    }
  });
  const [referralsEnabled, setReferralsEnabled] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem('neuranet-muted') === 'true';
    } catch {
      return false;
    }
  });

  // Check if referrals feature is enabled
  useEffect(() => {
    const checkReferralsFeature = async () => {
      try {
        const isEnabled = await FeatureService.isFeatureEnabled('referrals');
        console.log('Referrals feature enabled:', isEnabled);
        setReferralsEnabled(isEnabled);
      } catch (error) {
        console.error('Error checking referrals feature:', error);
        setReferralsEnabled(false);
      }
    };
    checkReferralsFeature();
  }, [profile]); // Re-check when profile changes

  // Initialize audio manager and refresh profile data when sidebar is opened
  useEffect(() => {
    const initAudio = async () => {
      try {
        await audioManager.init();
        // Set initial volume after initialization
        const savedVolume = audioManager.getVolume();
        setVolume(savedVolume);
        setIsMuted(savedVolume === 0);
      } catch (error) {
        console.error('Failed to initialize audio:', error);
      }
    };

    initAudio();

    if (profile) {
      accountStore.refreshProfile();
    }
  }, []); // Empty dependency array means this runs once when component mounts

  const handleVolumeChange = async (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    localStorage.setItem('neuranet-muted', (newVolume === 0).toString());
    
    // Ensure audio is initialized before setting volume
    if (!audioManager.isInitialized()) {
      await audioManager.init();
    }
    
    audioManager.setVolume(newVolume);
  };

  const handleVolumeChangeComplete = async () => {
    // Play sound only when the user releases the slider
    if (!isMuted && volume > 0) {
      audioManager.playSelect();
    }
  };

  const handleMuteToggle = async () => {
    if (!audioManager.isInitialized()) {
      await audioManager.init();
    }

    if (isMuted) {
      // Unmute: restore previous volume
      const volumeToRestore = prevVolume || 1;
      setVolume(volumeToRestore);
      setIsMuted(false);
      audioManager.setVolume(volumeToRestore);
      localStorage.setItem('neuranet-muted', 'false');
      audioManager.playSelect();
    } else {
      // Mute: save current volume and set to 0
      setPrevVolume(volume);
      localStorage.setItem('neuranet-prev-volume', volume.toString());
      setVolume(0);
      setIsMuted(true);
      audioManager.setVolume(0);
      localStorage.setItem('neuranet-muted', 'true');
    }
  };

  return (
    <>
      {showReferralDashboard && (
        <ReferralDashboard onClose={() => {
          setShowReferralDashboard(false);
          audioManager.playSelect();
        }} />
      )}

      {showAdminDashboard && (
        <AdminDashboard
          onClose={() => {
            setShowAdminDashboard(false);
            audioManager.playSelect();
          }}
          currentUser={profile}
        />
      )}

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          zIndex: 9998,
          animation: 'fadeIn 0.2s ease-out'
        }}
      />

      {/* Sidebar */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '320px',
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.98)',
        borderLeft: '2px solid #33ff33',
        boxShadow: '-10px 0 30px rgba(51, 255, 51, 0.15)',
        color: '#33ff33',
        fontFamily: 'IBM BIOS, monospace',
        animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)'
      }}>
        {profile ? (
          <>
            {/* User Info */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid rgba(51, 255, 51, 0.3)',
              background: 'linear-gradient(180deg, rgba(51, 255, 51, 0.1) 0%, rgba(0, 0, 0, 0) 100%)'
            }}>
              <div style={{
                fontSize: '1.2em',
                textShadow: '0 0 8px #33ff33'
              }}>
                {profile.data.discordUsername}
              </div>
            </div>

            {/* Stats Panel */}
            <div style={{
              background: 'rgba(51, 255, 51, 0.05)',
              border: '1px solid rgba(51, 255, 51, 0.3)',
              margin: '20px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ opacity: 0.7 }}>RANK:</span>
                <span style={{
                  color: accountStore.getRankColor(),
                  textShadow: `0 0 8px ${accountStore.getRankColor()}`
                }}>
                  {accountStore.getRankName()}
                </span>
              </div>
              {profile.data.omegaClearance && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ opacity: 0.7 }}>CLEARANCE:</span>
                  <span style={{
                    color: '#33ff33',
                    textShadow: '0 0 8px #33ff33'
                  }}>
                    OMEGA-{profile.data.omegaClearance}
                  </span>
                </div>
              )}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ opacity: 0.7 }}>QUBITS:</span>
                  <span style={{
                    textShadow: '0 0 8px #33ff33',
                    fontSize: '1.2em'
                  }}>
                    {accountStore.getQubits().toLocaleString()}
                  </span>
                </div>
                {profile.data.qubitWindow && (
                  <div style={{
                    marginLeft: '16px',
                    marginTop: '2px',
                    fontSize: '0.85em',
                    opacity: 0.8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    borderLeft: '2px solid rgba(51, 255, 51, 0.2)',
                    paddingLeft: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        color: profile.data.qubitWindow.totalQubits > 15000 ? '#ff3333' : '#33ff33',
                        textShadow: `0 0 8px ${profile.data.qubitWindow.totalQubits > 15000 ? '#ff3333' : '#33ff33'}`
                      }}>
                        {(profile.data.qubitWindow.totalQubits >= 1000
                          ? (profile.data.qubitWindow.totalQubits/1000).toFixed(1) + 'K'
                          : profile.data.qubitWindow.totalQubits)}/20K
                      </span>
                    </div>
                    {(() => {
                      const now = Date.now();
                      const windowAge = now - profile.data.qubitWindow.startTime;
                      if (windowAge < 86400000) { // If window is still active
                        const timeLeft = 86400000 - windowAge;
                        const hoursLeft = Math.floor(timeLeft / 3600000);
                        const minutesLeft = Math.floor((timeLeft % 3600000) / 60000);
                        return (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{ color: '#33ff33' }}>
                              {hoursLeft}H {minutesLeft}M
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              padding: '0 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <button
                onClick={onShowLeaderboard}
                style={{
                  background: 'rgba(51, 255, 51, 0.05)',
                  border: '1px solid #33ff33',
                  color: '#33ff33',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'IBM BIOS, monospace',
                  fontSize: '14px',
                  letterSpacing: '1px'
                }}
                onMouseEnter={e => {
                  e.target.style.background = 'rgba(51, 255, 51, 0.1)';
                  e.target.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={e => {
                  e.target.style.background = 'rgba(51, 255, 51, 0.05)';
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                LEADERBOARDS
              </button>
              <button
                onClick={onShowStatistics}
                style={{
                  background: 'rgba(51, 255, 51, 0.05)',
                  border: '1px solid #33ff33',
                  color: '#33ff33',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontFamily: 'IBM BIOS, monospace',
                  fontSize: '14px',
                  letterSpacing: '1px'
                }}
                onMouseEnter={e => {
                  e.target.style.background = 'rgba(51, 255, 51, 0.1)';
                  e.target.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={e => {
                  e.target.style.background = 'rgba(51, 255, 51, 0.05)';
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 18h20M2 12h20M2 6h20" />
                </svg>
                STATISTICS
              </button>
              {referralsEnabled && (
                <button
                  onClick={() => {
                    setShowReferralDashboard(true);
                    audioManager.playSelect();
                  }}
                  style={{
                    background: 'rgba(51, 255, 51, 0.05)',
                    border: '1px solid #33ff33',
                    color: '#33ff33',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: 'IBM BIOS, monospace',
                    fontSize: '14px',
                    letterSpacing: '1px'
                  }}
                  onMouseEnter={e => {
                    e.target.style.background = 'rgba(51, 255, 51, 0.1)';
                    e.target.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={e => {
                    e.target.style.background = 'rgba(51, 255, 51, 0.05)';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  REFERRALS
                </button>
              )}
              {profile.data?.isAdmin && (
                <button
                  onClick={() => {
                    setShowAdminDashboard(true);
                    audioManager.playSelect();
                  }}
                  style={{
                    background: 'rgba(253, 105, 7, 0.05)',
                    border: '1px solid #FD6907',
                    color: '#FD6907',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: 'IBM BIOS, monospace',
                    fontSize: '14px',
                    letterSpacing: '1px'
                  }}
                  onMouseEnter={e => {
                    e.target.style.background = 'rgba(253, 105, 7, 0.1)';
                    e.target.style.transform = 'translateX(4px)';
                    e.target.style.boxShadow = '0 0 15px rgba(253, 105, 7, 0.3)';
                  }}
                  onMouseLeave={e => {
                    e.target.style.background = 'rgba(253, 105, 7, 0.05)';
                    e.target.style.transform = 'translateX(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                    <path d="M2 17l10 5 10-5"></path>
                    <path d="M2 12l10 5 10-5"></path>
                  </svg>
                  ADMIN PANEL
                </button>
              )}
            </div>

            {/* Footer */}
            <div style={{
              marginTop: 'auto',
              padding: '20px',
              borderTop: '1px solid rgba(51, 255, 51, 0.3)',
              background: 'linear-gradient(0deg, rgba(51, 255, 51, 0.1) 0%, rgba(0, 0, 0, 0) 100%)',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Volume Control */}
              <div style={{
                background: 'rgba(51, 255, 51, 0.05)',
                border: '1px solid rgba(51, 255, 51, 0.3)',
                borderRadius: '4px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {/* Speaker icon that toggles mute */}
                <div
                  onClick={handleMuteToggle}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: isMuted ? 0.5 : 1,
                    transition: 'opacity 0.2s'
                  }}
                >
                  {isMuted ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                      <line x1="23" y1="9" x2="17" y2="15"></line>
                      <line x1="17" y1="9" x2="23" y2="15"></line>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                    </svg>
                  )}
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  onMouseUp={handleVolumeChangeComplete}
                  onTouchEnd={handleVolumeChangeComplete}
                  style={{
                    flex: 1,
                    height: '2px',
                    WebkitAppearance: 'none',
                    background: `linear-gradient(to right, #33ff33 ${volume * 100}%, rgba(51, 255, 51, 0.2) ${volume * 100}%)`,
                    outline: 'none',
                    opacity: '0.7',
                    transition: 'opacity 0.2s',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ 
                  fontSize: '12px', 
                  opacity: 0.7,
                  minWidth: '36px',
                  textAlign: 'right'
                }}>
                  {Math.round(volume * 100)}%
                </span>
              </div>

              <button
                onClick={onLogout}
                style={{
                  background: 'rgba(255, 51, 51, 0.05)',
                  border: '1px solid #ff3333',
                  color: '#ff3333',
                  padding: '16px',
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontFamily: 'IBM BIOS, monospace',
                  fontSize: '14px',
                  letterSpacing: '1px'
                }}
                onMouseEnter={e => {
                  e.target.style.background = 'rgba(255, 51, 51, 0.1)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.target.style.background = 'rgba(255, 51, 51, 0.05)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                LOG-OUT
              </button>
            </div>
          </>
        ) : (
          // Login State
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: '24px'
          }}>
            <button
              onClick={() => {
                const authUrl = DiscordAuthService.getAuthUrl();
                window.location.replace(authUrl);
              }}
              style={{
                background: 'rgba(51, 255, 51, 0.05)',
                border: '1px solid #33ff33',
                color: '#33ff33',
                padding: '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                fontFamily: 'IBM BIOS, monospace',
                fontSize: '14px',
                letterSpacing: '1px'
              }}
              onMouseEnter={e => {
                e.target.style.background = 'rgba(51, 255, 51, 0.1)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.target.style.background = 'rgba(51, 255, 51, 0.05)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              ESTABLISH CONNECTION
            </button>
          </div>
        )}

        <style>
          {`
            @keyframes slideIn {
              from {
                transform: translateX(100%);
              }
              to {
                transform: translateX(0);
              }
            }

            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            input[type="range"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 12px;
              height: 12px;
              background: #33ff33;
              border: 2px solid #33ff33;
              border-radius: 50%;
              cursor: pointer;
              box-shadow: 0 0 8px #33ff33;
              transition: all 0.2s;
              margin-top: -5px;
            }

            input[type="range"]::-webkit-slider-thumb:hover {
              box-shadow: 0 0 12px #33ff33;
              transform: scale(1.1);
            }

            input[type="range"]::-moz-range-thumb {
              width: 12px;
              height: 12px;
              background: #33ff33;
              border: 2px solid #33ff33;
              border-radius: 50%;
              cursor: pointer;
              box-shadow: 0 0 8px #33ff33;
              transition: all 0.2s;
            }

            input[type="range"]::-moz-range-thumb:hover {
              box-shadow: 0 0 12px #33ff33;
              transform: scale(1.1);
            }

            input[type="range"]::-webkit-slider-runnable-track {
              height: 2px;
              border-radius: 1px;
            }

            input[type="range"]::-moz-range-track {
              height: 2px;
              border-radius: 1px;
            }
          `}
        </style>
      </div>
    </>
  );
};

export default ProfileSidebar;